import { isAnnotationBundleSet } from "@/api/trainingAnnotation";
import {
  AnnotationOperationError,
  type AnnotationFrameDraft,
  type AnnotationIndexedAuthoringDriver,
  type AnnotationMutationResult,
  type AnnotationOperationAvailability,
  type AnnotationOperationContext,
  type AnnotationSavedFrameMutationReceipt,
  type CapturedFrame,
  type CaptureFrameRequest,
  type DeleteAnnotationFrameRequest,
  type SaveAnnotationFrameRequest,
  type SegmentationResult,
  type SegmentRequest,
  type SegmentTargetsRequest,
} from "@carbon6/c6-frontend-design/annotation";
import {
  deleteAnnotationSetFrame,
  fetchAnnotationFrameBlob,
  getAnnotationFrameMeta,
  requestAnnotationPreview,
  saveAnnotationSetFrame,
  type AnnotationPreviewReq,
  type AnnotationPreviewResp,
  type AnnotationSetFrameMutationReceipt,
  type AnnotationSetFrameWriteReq,
  type AnnotationSetSummaryView,
} from "@/api";
import { gpuUnavailableForAnnotationReason } from "@/components/gpu";
import { RegistryAnnotationSavedFrameSource } from "../data/RegistryAnnotationSavedFrameSource";
import {
  AnnotationFrameGeometryRegistry,
  draftTargetToWire,
  frameIndexAtTimestamp,
  normalizeWireContour,
} from "../model/annotationCoordinates";
import { AnnotationIdentityRegistry } from "../model/annotationIdentity";
import {
  annotationAuthoringAvailability,
  toAnnotationOperationError,
} from "../model/annotationOperationError";
import {
  createAnnotationFrameDescriptor,
  decodeAnnotationImageSize,
  type AnnotationImageSize,
} from "./annotationFrameDescriptor";

export class RegistryAnnotationAuthoringDriver implements AnnotationIndexedAuthoringDriver {
  constructor(
    private readonly options: {
      datasetUuid: string;
      identities: AnnotationIdentityRegistry;
      geometry: AnnotationFrameGeometryRegistry;
      savedFrames: RegistryAnnotationSavedFrameSource;
      getSelectedSet: () => AnnotationSetSummaryView | null;
      onSetChanged: (set: AnnotationSetSummaryView) => void;
      // GPU holder 가 없어 상류가 비어 있는지 판정한다. 폴링 값이라 호출 사이에 바뀌므로 매 호출 때
      // 읽는다. 생략하면 게이트가 없다(테스트, 비대화형 경로).
      getCapacityBlocked?: () => boolean;
      getFrameMeta?: typeof getAnnotationFrameMeta;
      fetchFrame?: typeof fetchAnnotationFrameBlob;
      preview?: typeof requestAnnotationPreview;
      saveFrame?: typeof saveAnnotationSetFrame;
      deleteFrame?: typeof deleteAnnotationSetFrame;
      decodeImageSize?: (
        blob: Blob,
        options: { signal: AbortSignal },
      ) => Promise<AnnotationImageSize>;
      createDescriptor?: typeof createAnnotationFrameDescriptor;
    },
  ) {}

  getAvailability(_context: AnnotationOperationContext): AnnotationOperationAvailability {
    // GPU 부재를 세트 상태보다 먼저 판정한다. committed 사유는 복제로 해결할 수 있지만 복제해도
    // GPU 는 생기지 않는다. 그래서 먼저 알릴 것은 "지금은 아무것도 할 수 없다" 는 사실이다.
    if (this.options.getCapacityBlocked?.() === true) {
      return { allowed: false, localizedReason: gpuUnavailableForAnnotationReason };
    }
    if (isAnnotationBundleSet(this.options.getSelectedSet()))
      return {
        allowed: false,
        localizedReason: { en: "File bundles are read-only.", ko: "파일 번들은 읽기 전용입니다." },
      };
    return annotationAuthoringAvailability(this.options.getSelectedSet()?.status ?? null);
  }

  async captureFrame(
    request: CaptureFrameRequest,
    { signal }: { signal: AbortSignal },
  ): Promise<CapturedFrame> {
    try {
      if (signal.aborted) throw signal.reason;
      const episode = this.options.identities.episode(request.sequenceId);
      const camera = this.options.identities.camera(request.streamId);
      if (episode === null || camera === null) throw new Error("unknown annotation media identity");
      const getFrameMeta = this.options.getFrameMeta ?? getAnnotationFrameMeta;
      const meta = await getFrameMeta(this.options.datasetUuid, camera, episode, { signal });
      const frameIndex = frameIndexAtTimestamp(request.timestampSeconds, meta.fps, meta.num_frames);
      const fetchFrame = this.options.fetchFrame ?? fetchAnnotationFrameBlob;
      const blob = await fetchFrame(this.options.datasetUuid, camera, episode, frameIndex, {
        signal,
      });
      const decodeImageSize = this.options.decodeImageSize ?? decodeAnnotationImageSize;
      const size = await decodeImageSize(blob, { signal });
      if (signal.aborted) throw signal.reason;
      const frameId = this.options.identities.frameId(camera, episode, frameIndex);
      this.options.geometry.bind(frameId, size);
      const createDescriptor = this.options.createDescriptor ?? createAnnotationFrameDescriptor;
      return {
        frameId,
        sequenceId: request.sequenceId,
        streamId: request.streamId,
        timestampSeconds: frameIndex / meta.fps,
        sourceRevision: 1,
        width: size.width,
        height: size.height,
        frameSource: createDescriptor(blob, { id: `${frameId}:source`, revision: 1 }),
      };
    } catch (error) {
      throw toAnnotationOperationError("capture", error);
    }
  }

  async segment(
    request: SegmentRequest,
    options: { signal: AbortSignal },
  ): Promise<SegmentationResult> {
    const [result] = await this.segmentTargets(
      {
        frameId: request.frameId,
        sourceRevision: request.sourceRevision,
        targets: [request],
      },
      options,
    );
    if (result === undefined) {
      return {
        frameId: request.frameId,
        targetId: request.targetId,
        pointRevision: request.pointRevision,
        contours: [],
      };
    }
    return result;
  }

  async segmentTargets(
    request: SegmentTargetsRequest,
    { signal }: { signal: AbortSignal },
  ): Promise<readonly SegmentationResult[]> {
    try {
      if (signal.aborted) throw signal.reason;
      const identity = this.options.identities.frame(request.frameId);
      const size = this.options.geometry.get(request.frameId);
      if (identity === null || size === null) throw new Error("unknown annotation frame");
      const objects = request.targets.map((target) => {
        const objId = this.options.identities.objId(target.targetId);
        if (objId === null) throw new Error("unknown annotation target");
        return draftTargetToWire(objId, { points: target.points }, size);
      });
      const payload: AnnotationPreviewReq = {
        camera: identity.camera,
        episode: identity.episode,
        frame_index: identity.frameIndex,
        objects,
      };
      const preview = this.options.preview ?? requestAnnotationPreview;
      const response = await preview(this.options.datasetUuid, payload, { signal });
      return this.toSegmentationResults(request, response);
    } catch (error) {
      throw toAnnotationOperationError("segment", error);
    }
  }

  async saveAnnotationFrame(
    request: SaveAnnotationFrameRequest,
    { signal }: { signal: AbortSignal },
  ): Promise<AnnotationMutationResult<AnnotationSavedFrameMutationReceipt>> {
    try {
      if (signal.aborted) throw signal.reason;
      if (request.base === undefined) {
        throw new AnnotationOperationError("save", "annotation.set_required", {
          localizedUserMessage: {
            en: "Select an annotation set before saving.",
            ko: "저장할 Annotation Set를 먼저 선택해 주세요.",
          },
        });
      }
      this.assertSelectedBase(request.base.setId);
      const write = this.frameWrite(request.frame, request.base.revision);
      if (write.targets.length === 0) {
        // 새 캡처의 점을 모두 취소한 경우 서버에 삭제할 프레임이 없다.
        if (this.options.savedFrames.getFrameSummary(request.frame.frameId) === null) {
          return this.localReceipt(request.workspaceRevision);
        }
        return await this.deletePersistedFrame(
          request.base.setId,
          request.frame.frameId,
          request.base.revision,
          request.workspaceRevision,
          signal,
        );
      }

      const saveFrame = this.options.saveFrame ?? saveAnnotationSetFrame;
      const receipt = await saveFrame(request.base.setId, request.frame.frameId, write, { signal });
      this.publishMutation(receipt);
      return this.localReceipt(request.workspaceRevision);
    } catch (error) {
      throw toAnnotationOperationError("save", error);
    }
  }

  async deleteAnnotationFrame(
    request: DeleteAnnotationFrameRequest,
    { signal }: { signal: AbortSignal },
  ): Promise<AnnotationMutationResult<AnnotationSavedFrameMutationReceipt>> {
    try {
      if (signal.aborted) throw signal.reason;
      this.assertSelectedBase(request.base.setId);
      return await this.deletePersistedFrame(
        request.base.setId,
        request.frameId,
        request.base.revision,
        request.workspaceRevision,
        signal,
      );
    } catch (error) {
      throw toAnnotationOperationError("remove", error);
    }
  }

  private frameWrite(
    frame: AnnotationFrameDraft,
    expectedRevision: number,
  ): AnnotationSetFrameWriteReq {
    const identity = this.options.identities.frame(frame.frameId);
    if (identity === null) throw new Error("unknown annotation frame identity");
    this.options.geometry.bind(frame.frameId, { width: frame.width, height: frame.height });
    return {
      expected_revision: expectedRevision,
      camera: identity.camera,
      episode: identity.episode,
      frame_index: identity.frameIndex,
      targets: Object.values(frame.targets)
        .filter((target) => target.points.length > 0)
        .map((target) => {
          const objId = this.options.identities.objId(target.targetId);
          if (objId === null) throw new Error("unknown annotation target identity");
          return draftTargetToWire(objId, target, { width: frame.width, height: frame.height });
        }),
    };
  }

  private toSegmentationResults(
    request: SegmentTargetsRequest,
    response: AnnotationPreviewResp,
  ): SegmentationResult[] {
    const contoursByObject = new Map(
      response.contours.map((entry) => [entry.object_id, entry.polygons] as const),
    );
    const size = { width: response.width, height: response.height };
    this.options.geometry.bind(request.frameId, size);
    return request.targets.map((target) => {
      const objId = this.options.identities.objId(target.targetId);
      if (objId === null) throw new Error("unknown annotation target identity");
      return {
        frameId: request.frameId,
        targetId: target.targetId,
        pointRevision: target.pointRevision,
        contours: (contoursByObject.get(objId) ?? []).map((polygon) =>
          normalizeWireContour(polygon, size),
        ),
      };
    });
  }

  private async deletePersistedFrame(
    setUuid: string,
    frameId: string,
    expectedRevision: number,
    workspaceRevision: number,
    signal: AbortSignal,
  ): Promise<AnnotationMutationResult<AnnotationSavedFrameMutationReceipt>> {
    const deleteFrame = this.options.deleteFrame ?? deleteAnnotationSetFrame;
    const receipt = await deleteFrame(setUuid, frameId, expectedRevision, { signal });
    this.publishMutation(receipt);
    return this.localReceipt(workspaceRevision);
  }

  private publishMutation(receipt: AnnotationSetFrameMutationReceipt): void {
    this.options.savedFrames.applyMutation(receipt);
    const selected = this.options.getSelectedSet();
    if (selected?.uuid !== receipt.set_uuid) return;
    const index = this.options.savedFrames.getSnapshot();
    const targetPairs = new Set(
      index.orderedFrames.flatMap((frame) =>
        frame.annotatedTargetIds.map((targetId) => `${frame.streamId}:${targetId}`),
      ),
    );
    this.options.onSetChanged({
      ...selected,
      revision: receipt.set_revision,
      annotated_frame_count: index.orderedFrames.length,
      annotated_target_count: targetPairs.size,
    });
  }

  private assertSelectedBase(setId: string): void {
    const selected = this.options.getSelectedSet();
    if (selected?.uuid !== setId || selected.status !== "draft") {
      throw new AnnotationOperationError("save", "training.annotation_set_revision_conflict", {
        conflict: true,
      });
    }
  }

  private localReceipt(
    workspaceRevision: number,
  ): AnnotationMutationResult<AnnotationSavedFrameMutationReceipt> {
    const snapshot = this.options.savedFrames.getSnapshot();
    return {
      workspaceRevision,
      entity: {
        set: snapshot.set,
        savedFrameIndexRevision: snapshot.revision,
      },
    };
  }
}
